import React from "react";
import { Text, Button, Menu } from "@fragments";
import { useScreen } from "@hooks/useScreen";
import {
  IconX,
  IconLayoutSidebarLeftExpand,
  IconSun,
  IconMoon,
  IconWorld,
  IconCreditCard,
} from "@tabler/icons-react";
import StudioBrand from "../StudioBrand";
import StudioCredits from "../StudioCredits";
import ProfileSwitcher from "./components/ProfileSwitcher";
import UserSelect from "Screen/Sidebar/userSelect";
import { STUDIO_PLAN } from "../../enums/routes";
import memoStudio from "../../utils/memoStudio";
import "./styles.scss";

function StudioHeader({
  studio: s,
  mobile,
  mobileOpen,
  sidebarOpen,
  openSidebar,
  closeMobile,
}) {
  const { t, auth, navigate, goToLogin } = s;
  const { resolvedAppearance, setAppearance, setLanguage, language } =
    useScreen();
  const brand = <StudioBrand onClick={closeMobile} />;
  // The account menu (hover on the avatar) leads to the plan page while payments are on.
  const accountItems = s.features.payments
    ? [{ id: "subscriptions", label: t("manageSubscriptions"), icon: <IconCreditCard />, onClick: () => navigate(STUDIO_PLAN) }]
    : [];
  return (
    <header className="studio-header">
      <span className="studio-header__blur" aria-hidden="true"><i /><i /><i /></span>
      <div className="studio-header__leading">
        {auth.signed ? (
          <>
            {mobile && (
              <Button
                color="overlay"
                variant="rounded"
                discreet
                icon={<IconLayoutSidebarLeftExpand />}
                aria-label={t("menu")}
                aria-expanded={mobileOpen}
                aria-controls="studio-sidebar"
                tooltip={t("menu")}
                onClick={openSidebar}
              />
            )}
            {!s.current && (!sidebarOpen || mobile) && brand}
            <ProfileSwitcher studio={s} compact={mobile} />
            {s.current && (
              <Text.h4 contrast={8} weight={400} maxLines={1}>
                {s.current.title}
              </Text.h4>
            )}
          </>
        ) : (
          brand
        )}
      </div>
      {!auth.signed && (
        <nav className="studio-public-nav">
          <Button color="overlay" variant="text" onClick={goToLogin}>
            {t("communities")}
          </Button>
          <Button
            color="overlay"
            variant="text"
            onClick={() =>
              window.open(
                "https://help.ginastee.com",
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            {t("support")}
          </Button>
        </nav>
      )}
      <div className="studio-header__actions">
        {auth.signed && <StudioCredits studio={s} />}
        <Menu
          items={["pt", "en", "es"].map((code, index) => ({
            label: ["Português", "English", "Español"][index],
            onClick: () => setLanguage(code),
          }))}
        >
          <Button
            className="studio-header__language"
            variant="text"
            color="overlay"
            startIcon={<IconWorld />}
            aria-label={t("language")}
            size="small"
          >
            {language?.toUpperCase()}
          </Button>
        </Menu>
        <Button
          className="studio-header__theme"
          color="overlay"
          variant="text"
          size="small"
          icon={resolvedAppearance === "dark" ? <IconMoon /> : <IconSun />}
          aria-label={t("theme")}
          tooltip={t("theme")}
          onClick={() =>
            setAppearance(resolvedAppearance === "dark" ? "light" : "dark")
          }
        />
        {auth.signed && <UserSelect variant="avatar" items={accountItems} />}
        {!auth.signed && (
          <Button color="inverse" size="small" onClick={goToLogin}>
            {t("login")}
          </Button>
        )}
      </div>
      {auth.signed && (
        <div className="studio-exit-corner">
          <Button
            className="studio-exit"
            variant="rounded"
            color="inverse"
            {...(mobile ? { icon: <IconX /> } : { endIcon: <IconX /> })}
            aria-label={t("exitStudio")}
            tooltip={t("exitStudio")}
            onClick={() => navigate("/")}
          >
            <Text.label>{t("exitStudio")}</Text.label>
          </Button>
        </div>
      )}
    </header>
  );
}

// Every `studio` field the header, the profile switcher and the credits panel read.
export default memoStudio(StudioHeader, ["t", "locale", "auth", "current", "credits", "features", "capabilities", "accounts", "onboarding", "payments",
  "goToLogin", "navigate", "setModal", "snapshot.usage"]);
